<template>
  <div v-if="!model.$disabled">
    <w-label
      :model="model"
      :description="description"
      :next-number="nextNumber"
      :bold-label="boldLabel"
      :title="titleValue"
      :show-label="showLabelValue"
      :labelFor="'vSelect-' + sid"
      :only-description="onlyDescription"
    >
      <template v-slot:description>
        <slot name="description" />
      </template>
    </w-label>

    <v-select
      :inputId="'vSelect-' + sid"
      :class="{'is-invalid': this.model.$error}"
      :closeOnSelect="closeOnSelect"
      :disabled="isDisabled"
      :options="options"
      :value="localValues"
      :name="model.$form.testPath ? model.$path : undefined"
      :multiple="isModelAnArray"
      :searchable="taggable || customValue ? true : searchable"
      :placeholder="placeholder"
      :clearSearchOnSelect="taggable ? true : clearSearchOnSelect"
      :taggable="taggable || customValue"
      :autocomplete="autocomplete"
      :aria-label="checkLabel"
      :aria-invalid="isError"
      :aria-describedby="`err-${sid} warn-${sid}`"
      @search:focus="focus"
      @search:blur="blur"
      @input="selectedValue"
    >
      <template name="no-options">
        <slot slot="no-options">
          <span>{{ noOptionsText }}</span>
        </slot>
      </template>

      <template v-slot:selected-option="option" v-if="this.$scopedSlots['selected-option']">
        <slot name="selected-option" :child="option">{{ option.label }}</slot>
      </template>

      <template v-slot:selected-option="option" v-else>
        <slot name="option" :child="option">{{ option.label }}</slot>
      </template>

      <template v-slot:option="option">
        <slot name="option" :child="option">{{ option.label }}</slot>
      </template>
    </v-select>

    <w-error :model="model" :id="'err-' + sid" />

    <w-warning :model="model" :id="`warn-${sid}`" />
  </div>
</template>

<script>
import 'vue-select/dist/vue-select.css';

import vSelect from 'vue-select';
import titleMixin from '../mixins/titleMixin';
import modelMixin from '../mixins/modelMixin';
import WError from './error.vue';
import WTooltip from './tooltip.vue';

export default {
  name: 'vue-select',
  components: {WError, WTooltip, vSelect},
  mixins: [modelMixin, titleMixin],
  props: {
    values: Array,
    label: {
      type: [String, Boolean],
      default: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    placeholder: String,
    clearSearchOnSelect: {
      type: Boolean,
      default: true
    },
    setTitleAsPlaceholder: {
      type: Boolean,
      default: false
    },
    noOptionsText: {
      type: String,
      default: 'Przepraszamy, nie znaleziono pasującej opcji.'
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    mapObject: {
      type: Function,
      default: (option) => ({text: option.label, value: option.code})
    },
    objectValueProperty: {
      type: String,
      default: 'value'
    },
    customValue: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sid: Math.round(Math.random() * 1000000)
    };
  },
  computed: {
    titleValue() {
      return typeof this.label === 'string' ? this.label : this.title;
    },
    showLabelValue() {
      return typeof this.label === 'boolean' ? this.label : this.showLabel;
    },
    checkLabel() {
      if (this.label && this.model.$schema.title === undefined) {
        return this.aria;
      }

      return undefined;
    },
    isModelAnArray() {
      return this.model.$schema.type === 'array';
    },
    isModelAnObj() {
      return this.model.$schema.type === 'object';
    },
    isChildAnObject() {
      return this.model.$schema.itemType === 'object';
    },
    taggable() {
      return this.isModelAnArray;
    },
    options() {
      if (!this.values) {
        return [];
      }

      return this.values.map((item) => {
        if (typeof item === 'object') {
          return {label: item.text || item.value, code: item.value};
        } else {
          return {label: item, code: item};
        }
      });
    },
    localValues() {
      let values = [];

      if (this.isModelAnObj) {
        values.push(this.svalue[this.objectValueProperty]);
      } else if (this.isModelAnArray) {
        if (this.isChildAnObject) {
          values = this.svalue.map((value) => value[this.objectValueProperty]);
        } else {
          values = [...this.svalue];
        }
      } else {
        values.push(this.svalue);
      }

      return this.getOptionsByValues(values);
    }
  },
  methods: {
    // Blur na polu schemy
    blur() {
      this.$set(this.model, '$hasFocus', false);
    },
    // Focus na polu schemy
    focus() {
      this.$set(this.model, '$hasFocus', true);
    },

    getOptionsByValues(values) {
      const options = this.options.filter((x) => values.some((y) => y.toString() === x.code.toString()));
      if (!this.customValue || options.length === values.length) {
        return options;
      }

      values = values.filter((value) => value && !options.some((option) => option.code.toString() === value.toString())).map((value) => ({code: value, label: value}));
      return options.concat(values);
    },

    // Emit z wybraną wartością
    selectedValue(values) {
      if (values === void 0) {
        values = [];
      }

      if (!Array.isArray(values)) {
        values = [values];
      }

      // jeśli  usuwamy opcję to niektóre opcje mogą być nullem, więc wyczyśćmy te, które są puste
      values = values.filter((x) => x !== null);

      // w momencie gdy wpisujemy wartość z 'palca', nie dostaje ona property `code`
      for (let i = 0; i < values.length; i++) {
        if (typeof values[i] === 'string') {
          values[i] = {code: values[i], label: values[i]};
        } else {
          values[i].code = values[i].code || values[i].label;
        }
      }

      values = this.getOptionsByValues(values.map((value) => value.code));

      if (this.isModelAnArray) {
        for (let i = this.model.$items.length - 1; i >= 0; i--) {
          this.model.$removeRow(i);
        }

        this.$emit('rowDeleted');

        for (let i = 0; i < values.length; i++) {
          if (this.isChildAnObject) {
            this.model.$addRow(this.mapObject(values[i]));
          } else {
            this.model.$addRow(values[i].code);
          }
        }

        this.$emit('rowAdded', values.length);
      } else if (this.isModelAnObj) {
        this.$set(this.model, '$value', this.mapObject(values[0]));
      } else {
        if (values.length) {
          this.$set(this.model, '$value', values[0].code + '');
        } else {
          this.$set(this.model, '$value', '');
        }
      }

      this.$emit('input', this.model.$value);
    }
  },
  mounted() {
    if (this.setTitleAsPlaceholder) {
      this.placeholder = this.getTitle;
      this.label = false;
    }
    console.log(this.$scopedSlots);
  }
};
</script>
<style>
/* skopiowane  ../../node_modules/vue-select/dist/vue-select.css*/
.v-select {
  position: relative;
  font-family: inherit;
}
.v-select,
.v-select * {
  box-sizing: border-box;
}
@-webkit-keyframes vSelectSpinner {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(1turn);
    transform: rotate(1turn);
  }
}
@keyframes vSelectSpinner {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(1turn);
    transform: rotate(1turn);
  }
}
.vs__fade-enter-active,
.vs__fade-leave-active {
  transition: opacity 0.15s cubic-bezier(1, 0.5, 0.8, 1);
}
.vs__fade-enter,
.vs__fade-leave-to {
  opacity: 0;
}
.vs--disabled .vs__clear,
.vs--disabled .vs__dropdown-toggle,
.vs--disabled .vs__open-indicator,
.vs--disabled .vs__search,
.vs--disabled .vs__selected {
  cursor: not-allowed;
  background-color: #f8f8f8;
}
.v-select[dir='rtl'] .vs__actions {
  padding: 0 3px 0 6px !important;
}
.v-select[dir='rtl'] .vs__clear {
  margin-left: 6px !important;
  margin-right: 0 !important;
}
.v-select[dir='rtl'] .vs__deselect {
  margin-left: 0 !important;
  margin-right: 2px !important;
}
.v-select[dir='rtl'] .vs__dropdown-menu {
  text-align: right;
}
.vs__dropdown-toggle {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  display: flex;
  padding: 0 0 4px !important;
  background: none;
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-radius: 4px;
  white-space: normal;
}
.vs__selected-options {
  display: flex;
  flex-basis: 100%;
  flex-grow: 1;
  flex-wrap: wrap;
  padding: 0 2px !important;
  position: relative;
}
.vs__actions {
  display: flex;
  align-items: center;
  padding: 4px 6px 0 3px !important;
}
.vs--searchable .vs__dropdown-toggle {
  cursor: text;
}
.vs--unsearchable .vs__dropdown-toggle {
  cursor: pointer;
}
.vs--open .vs__dropdown-toggle {
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.vs__open-indicator {
  fill: rgba(60, 60, 60, 0.5);
  -webkit-transform: scale(1);
  transform: scale(1);
  transition: -webkit-transform 0.15s cubic-bezier(1, -0.115, 0.975, 0.855);
  transition: transform 0.15s cubic-bezier(1, -0.115, 0.975, 0.855);
  transition: transform 0.15s cubic-bezier(1, -0.115, 0.975, 0.855), -webkit-transform 0.15s cubic-bezier(1, -0.115, 0.975, 0.855);
  transition-timing-function: cubic-bezier(1, -0.115, 0.975, 0.855);
}
.vs--open .vs__open-indicator {
  -webkit-transform: rotate(180deg) scale(1);
  transform: rotate(180deg) scale(1);
}
.vs--loading .vs__open-indicator {
  opacity: 0;
}
.vs__clear {
  fill: rgba(60, 60, 60, 0.5);
  padding: 0 !important;
  border: 0;
  background-color: transparent;
  cursor: pointer;
  margin-right: 8px !important;
}
.vs__dropdown-menu {
  display: block;
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  z-index: 1000;
  padding: 5px 0 !important;
  margin: 0 !important;
  width: 100%;
  max-height: 350px;
  min-width: 160px;
  overflow-y: auto;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-top-style: none;
  border-radius: 0 0 4px 4px;
  text-align: left;
  list-style: none;
  background: #fff;
}
.vs__no-options {
  text-align: center;
}
.vs__dropdown-option {
  line-height: 1.42857143;
  display: block;
  padding: 3px 20px !important;
  clear: both;
  color: #333;
  white-space: nowrap;
}
.vs__dropdown-option:hover {
  cursor: pointer;
}
.vs__dropdown-option--highlight {
  background: #5897fb;
  color: #fff;
}
.vs__selected {
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-radius: 4px;
  color: #333;
  line-height: 1.4;
  margin: 4px 2px 0 !important;
  padding: 0 0.25em !important;
}
.vs__deselect {
  display: inline-flex;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  margin-left: 4px !important;
  padding: 0 !important;
  border: 0;
  cursor: pointer;
  background: none;
  fill: rgba(60, 60, 60, 0.5);
  text-shadow: 0 1px 0 #fff;
}
.vs--single .vs__selected {
  background-color: transparent;
  border-color: transparent;
}
.vs--single.vs--open .vs__selected {
  position: absolute;
  opacity: 0.4;
}
.vs--single.vs--searching .vs__selected {
  display: none;
}
.vs__search::-ms-clear,
.vs__search::-webkit-search-cancel-button,
.vs__search::-webkit-search-decoration,
.vs__search::-webkit-search-results-button,
.vs__search::-webkit-search-results-decoration {
  display: none;
}
.vs__search,
.vs__search:focus {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  line-height: 1.4;
  font-size: 1em;
  border: 1px solid transparent;
  border-left: none;
  outline: none;
  margin: 4px 0 0 !important;
  padding: 0 7px !important;
  background: none;
  box-shadow: none;
  width: 0;
  max-width: 100%;
  flex-grow: 1;
}
.vs__search::-webkit-input-placeholder {
  color: inherit;
}
.vs__search:-ms-input-placeholder {
  color: inherit;
}
.vs__search::-ms-input-placeholder {
  color: inherit;
}
.vs__search::placeholder {
  color: inherit;
}
.vs--unsearchable .vs__search {
  opacity: 1;
}
.vs--unsearchable .vs__search:hover {
  cursor: pointer;
}
.vs--single.vs--searching:not(.vs--open):not(.vs--loading) .vs__search {
  opacity: 0.2;
}
.vs__spinner {
  align-self: center;
  opacity: 0;
  font-size: 5px;
  text-indent: -9999em;
  overflow: hidden;
  border: 0.9em solid hsla(0, 0%, 39.2%, 0.1);
  border-left-color: rgba(60, 60, 60, 0.45);
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation: vSelectSpinner 1.1s linear infinite;
  animation: vSelectSpinner 1.1s linear infinite;
  transition: opacity 0.1s;
}
.vs__spinner,
.vs__spinner:after {
  border-radius: 50%;
  width: 5em;
  height: 5em;
}
.vs--loading .vs__spinner {
  opacity: 1;
}

/*# sourceMappingURL=vue-select.css.map*/
/*custom styles*/
.vs__dropdown-toggle {
  border-radius: 0 !important;
  border-color: #bdbdbd !important;
}
.is-invalid .vs__dropdown-toggle {
  border-color: #b94a48 !important;
  background: #fff0f0 !important;
}
.noborderdisabled td.input .v-select .vs__dropdown-toggle {
  border: 0 !important;
}
.noborderdisabled td.input .v-select .vs__dropdown-menu {
  width: calc(100% + 8px);
  left: -3.5px;
}
.smart-form .input input.vs__search {
  width: 0;
  height: auto;
}
</style>
